import type { ActivityKind, ActivityModule, Treatment } from './types';

/**
 * Maps an activity kind - and how it should be drawn - to the code that renders
 * it.
 *
 * This is the seam that makes the week data-driven. The player never imports
 * an activity directly; it looks the kind up here. So a kind is one entry plus
 * one file, and no kind knows which animal it is showing: the same `feed`
 * serves the cat's bowl, the dog's bowl and the rabbit's basket, which is the
 * spec's own test of the modular build ("if modularity works, Layar 4, 5, 7,
 * 11 and 14 run for the dog with no new code").
 *
 * Every entry is a dynamic import, so each screen is its own chunk and the 3D
 * engine is only downloaded by someone who switches to it.
 */
type TreatmentMap = Partial<Record<Treatment, () => Promise<ActivityModule>>>;

const loaders: Record<ActivityKind, TreatmentMap> = {
  reveal: { flat: () => import('./activities/reveal') },
  bubbles: { flat: () => import('./activities/bubbles') },
  observe: { flat: () => import('./activities/observe') },
  'find-parts': { flat: () => import('./activities/find-parts') },
  'count-parts': { flat: () => import('./activities/count-parts') },
  compare: { flat: () => import('./activities/compare') },
  'sound-sort': { flat: () => import('./activities/sound-sort') },
  'listen-and-pick': { flat: () => import('./activities/listen-and-pick') },
  feelings: { flat: () => import('./activities/feelings') },
  move: { flat: () => import('./activities/move') },
  calm: { flat: () => import('./activities/calm') },
  feed: { flat: () => import('./activities/feed') },
  'sort-into': { flat: () => import('./activities/sort-into') },
  'place-match': { flat: () => import('./activities/place-match') },
  sequence: { flat: () => import('./activities/sequence') },
  safety: { flat: () => import('./activities/safety') },
  gentle: { flat: () => import('./activities/gentle') },
  table: { flat: () => import('./activities/table') },
  colour: { flat: () => import('./activities/colour') },
  name: { flat: () => import('./activities/name') },
  yard: { flat: () => import('./activities/yard') },

  hotspots: {
    flat: () => import('./activities/hotspots'),
    parallax: () => import('./activities/hotspots-parallax'),
    depth: () => import('./activities/hotspots-3d'),
  },
};

/**
 * Falls back to `flat` rather than failing. A treatment is a way of drawing a
 * lesson, so a missing one should cost the flourish, never the lesson.
 */
export function loadActivity(
  kind: ActivityKind,
  treatment: Treatment = 'flat',
): Promise<ActivityModule> | undefined {
  const entry = loaders[kind];
  return (entry?.[treatment] ?? entry?.flat)?.();
}

/** Which treatments this kind actually has, `flat` first. */
export function treatmentsFor(kind: ActivityKind): Treatment[] {
  const order: Treatment[] = ['flat', 'parallax', 'depth'];
  return order.filter((t) => loaders[kind]?.[t]);
}
